/**
 * A phone number with its country, rather than a text box and a hope.
 *
 * **The field it replaces asked for E.164 and accepted anything.** Typing a
 * number the way people actually write it at home — `050 000 0000` — produced
 * a perfectly valid-looking request, a `204`, a screen advancing to "enter the
 * code", and no SMS ever arriving. The endpoint answers `204` for every number
 * by design (`otp.ts`: telling somebody a number is not registered answers the
 * enumeration question one step removed), so nothing downstream could report
 * the mistake. The only place it can be caught is here, before it is sent.
 *
 * So the country is **chosen, not typed**, and the value handed up is always
 * `+<dial><digits>`.
 *
 * ## Why the list is short, and how it grows
 *
 * ~240 countries with flags and formats is a dependency, and this needs none
 * of it: the list below is the places MyRave has festivals or visitors, and
 * any other country is reachable by typing a full `+…` number, which the field
 * detects and accepts. A missing country is an inconvenience, not a wall.
 *
 * Add a row when a festival needs one. Order is by dial code so duplicates are
 * obvious — several countries share `+1`, which is why the value is the ISO
 * code and not the dial code.
 *
 * ## The flag is an emoji, deliberately
 *
 * Two regional-indicator characters, derived from the ISO code. No image, no
 * sprite sheet, nothing to load, and it inherits the text colour and size. A
 * platform without the glyphs shows the two letters instead, which is a
 * perfectly good fallback and is what Windows does.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from '@/app/LocaleProvider';
import { useT } from '@/i18n';
import './PhoneField.css';

/**
 * A country's name in the reader's language, from the platform's own table
 * (`Intl.DisplayNames`) — no list of translations to keep. The English name
 * below is the fallback where the runtime has none.
 */
function regionName(locale: string, c: { iso: string; name: string }): string {
  if (locale === 'en') return c.name;
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(c.iso) ?? c.name;
  } catch {
    return c.name;
  }
}

interface Country {
  /** ISO 3166-1 alpha-2. The identity, because dial codes are not unique. */
  iso: string;
  name: string;
  dial: string;
  /**
   * How somebody writes their number AT HOME, trunk zero and all.
   *
   * A placeholder's job is recognition, not correctness: an Israeli knows
   * `054-000-0000` on sight and would not recognise `54-000-0000`, even though
   * the second is what actually travels. The field strips the zero, so showing
   * the familiar form costs nothing and the unfamiliar one costs a pause.
   *
   * Only where it differs usefully from the generic. Absent is fine.
   */
  example?: string;
}

/**
 * Sorted by name within dial code. Israel first is not favouritism — it is
 * where the people testing this are, and a default that is usually right saves
 * more taps than an alphabetical one.
 */
const COUNTRIES: Country[] = [
  { example: '054-000-0000', iso: 'IL', name: 'Israel', dial: '+972' },
  { example: '07700 900123', iso: 'GB', name: 'United Kingdom', dial: '+44' },
  { example: '(202) 555-0100', iso: 'US', name: 'United States', dial: '+1' },
  { example: '06 20 123 4567', iso: 'HU', name: 'Hungary', dial: '+36' },
  { example: '0151 23456789', iso: 'DE', name: 'Germany', dial: '+49' },
  { example: '06 12345678', iso: 'NL', name: 'Netherlands', dial: '+31' },
  { example: '06 12 34 56 78', iso: 'FR', name: 'France', dial: '+33' },
  { example: '612 34 56 78', iso: 'ES', name: 'Spain', dial: '+34' },
  { example: '312 345 6789', iso: 'IT', name: 'Italy', dial: '+39' },
  { iso: 'PT', name: 'Portugal', dial: '+351' },
  { iso: 'PL', name: 'Poland', dial: '+48' },
  { iso: 'CZ', name: 'Czechia', dial: '+420' },
  { iso: 'AT', name: 'Austria', dial: '+43' },
  { iso: 'CH', name: 'Switzerland', dial: '+41' },
  { iso: 'BE', name: 'Belgium', dial: '+32' },
  { iso: 'SE', name: 'Sweden', dial: '+46' },
  { iso: 'NO', name: 'Norway', dial: '+47' },
  { iso: 'DK', name: 'Denmark', dial: '+45' },
  { iso: 'FI', name: 'Finland', dial: '+358' },
  { iso: 'IE', name: 'Ireland', dial: '+353' },
  { iso: 'GR', name: 'Greece', dial: '+30' },
  { iso: 'RO', name: 'Romania', dial: '+40' },
  { iso: 'BG', name: 'Bulgaria', dial: '+359' },
  { iso: 'HR', name: 'Croatia', dial: '+385' },
  { iso: 'RS', name: 'Serbia', dial: '+381' },
  { iso: 'TR', name: 'Türkiye', dial: '+90' },
  { iso: 'UA', name: 'Ukraine', dial: '+380' },
  { iso: 'RU', name: 'Russia', dial: '+7' },
  { iso: 'CA', name: 'Canada', dial: '+1' },
  { iso: 'MX', name: 'Mexico', dial: '+52' },
  { iso: 'BR', name: 'Brazil', dial: '+55' },
  { iso: 'AR', name: 'Argentina', dial: '+54' },
  { iso: 'AU', name: 'Australia', dial: '+61' },
  { iso: 'NZ', name: 'New Zealand', dial: '+64' },
  { iso: 'ZA', name: 'South Africa', dial: '+27' },
  { iso: 'IN', name: 'India', dial: '+91' },
  { iso: 'JP', name: 'Japan', dial: '+81' },
  { iso: 'TH', name: 'Thailand', dial: '+66' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '+971' },
];

/** `IL` → 🇮🇱. Two regional indicators, offset from 'A'. */
function flag(iso: string): string {
  return String.fromCodePoint(
    ...[...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}

/**
 * The E.164 number, or null while it cannot be one.
 *
 * `null` is what disables the button. The server would reject the same input
 * with a `204` that says nothing, so the judgement has to be made here.
 */
export function e164(country: Country, local: string): string | null {
  // A leading zero is the national trunk prefix — `054…` is `+97254…`, never
  // `+9720 54…`. Dropping it is the single most common correction this field
  // makes, and getting it wrong sends a code nowhere.
  const digits = local.replace(/\D/g, '').replace(/^0+/, '');
  if (!digits) return null;
  const full = `${country.dial}${digits}`;
  const count = full.replace(/\D/g, '').length;
  // E.164: at most 15 digits including the country code, and nothing usable is
  // shorter than about 7.
  return count >= 7 && count <= 15 ? full : null;
}

export function PhoneField({
  value, onChange, disabled, label, hint,
}: {
  /** The E.164 number, or '' while it is incomplete. */
  value: string;
  onChange: (e164OrEmpty: string) => void;
  disabled?: boolean;
  /**
   * Rendered as a REAL label on the number input, not a heading beside it.
   *
   * The completion gate first placed its own `<span>` above this component, and
   * a span is not a label: a screen reader announces "Phone number without the
   * country code" and never the thing being asked for. `getByLabelText` could
   * not find it either, which is how it was noticed — the accessibility bug and
   * the test failure were the same bug.
   */
  label?: string;
  /** A line under the field, in the same place `Field` puts one. */
  hint?: string;
}) {
  const tr = useT();
  const { locale } = useLocale();
  const [country, setCountry] = useState<Country>(COUNTRIES[0]!);
  const [local, setLocal] = useState('');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrap = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);

  // Report upward whenever either half changes. '' while incomplete, so the
  // caller never has to ask whether what it holds is sendable.
  useEffect(() => {
    onChange(e164(country, local) ?? '');
  }, [country, local, onChange]);

  useEffect(() => {
    if (open) search.current?.focus();
  }, [open]);

  /** Escape and a click outside, both of which people expect of a menu. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) =>
      c.name.toLowerCase().includes(q)
      || regionName(locale, c).toLowerCase().includes(q)
      || c.dial.includes(q.replace(/^\+?/, '+'))
      || c.iso.toLowerCase() === q);
  }, [query, locale]);

  /**
   * Someone pasting a full `+…` number gets the country picked for them.
   *
   * Longest dial code first, or `+1` would claim `+1…` before `+1`-sharing
   * codes are considered and `+972` would never be reached past `+97`.
   */
  function accept(raw: string) {
    if (raw.trim().startsWith('+')) {
      const digits = `+${raw.replace(/\D/g, '')}`;
      const found = [...COUNTRIES]
        .sort((a, b) => b.dial.length - a.dial.length)
        .find((c) => digits.startsWith(c.dial));
      if (found) {
        setCountry(found);
        setLocal(digits.slice(found.dial.length));
        return;
      }
    }
    setLocal(raw);
  }

  const complete = Boolean(value);
  const countryName = regionName(locale, country);

  return (
    <div ref={wrap} className="phone-field">
      <span className="phone-field-label">{label ?? tr('Phone number')}</span>

      {/* A phone number reads left to right in every language, dial code first. */}
      <div dir="ltr" className="phone-field-box" data-complete={complete || undefined}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={tr('Country: {country} {dial}', { country: countryName, dial: country.dial })}
          className="phone-field-picker"
        >
          <span aria-hidden="true" className="phone-field-flag">{flag(country.iso)}</span>
          <span>{country.iso} {country.dial}</span>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" className="phone-field-muted">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          disabled={disabled}
          value={local}
          onChange={(e) => accept(e.target.value)}
          placeholder={country.example ?? '500000000'}
          aria-label={label ?? tr('Phone number without the country code')}
          className="phone-field-number"
        />
      </div>

      <span className="phone-field-note">
        {local && !complete
          ? tr('That does not look like a full number yet.')
          : tr('Pick your country, then your number as you would write it at home.')}
      </span>

      {open && (
        <div role="listbox" aria-label={tr('Country')} className="phone-field-list">
          <div className="phone-field-search-bar">
            <input
              ref={search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tr('Search countries')}
              aria-label={tr('Search countries')}
              className="phone-field-search"
            />
          </div>

          {matches.length === 0
            ? (
              <div className="phone-field-none">
                {tr('No match. Type the full number with its + instead.')}
              </div>
            )
            : matches.map((c) => (
              <button
                key={c.iso}
                type="button"
                role="option"
                aria-selected={c.iso === country.iso}
                onClick={() => { setCountry(c); setOpen(false); setQuery(''); }}
                className="phone-field-option"
              >
                <span aria-hidden="true" className="phone-field-flag">{flag(c.iso)}</span>
                <span className="phone-field-option-name">{regionName(locale, c)}</span>
                <span dir="ltr" className="phone-field-option-dial">{c.dial}</span>
              </button>
            ))}
        </div>
      )}
      {hint && <span className="phone-field-hint">{hint}</span>}
    </div>
  );
}
